import { Suspense } from "react"
import {
  matchPath,
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams
} from "react-router-dom"

import { REDIRECT_URL_PARAM_AUTH } from "@/const/provider-auth.constants"
import { EXTERNAL_ROUTES, NAMES_ROUTES } from "@/const/routes.constants"
import { isAuthorizedUser } from "@/domain/user"
import { useCheckApplicationUpdates } from "@/hooks/useCheckUpdates"
import { useInitApp } from "@/hooks/useInitApp"
import { useLoggerOnNavigation } from "@/hooks/useLogger"
import { useServicesWithUser } from "@/hooks/useServicesWithUser"
import { useUser } from "@/hooks/useUser"
import { Loader } from "@/molecules/Loader"
import { KaiProvider } from "@/organisms/KaiChat"
import { Reports } from "@/organisms/Reports"
import { SplashScreen } from "@/organisms/SplashScreen"
import { TicketingProvider } from "@/organisms/Ticketing/hooks/useTicketing"
import { AiDiscovery } from "@/pages/AiDiscovery/AiDiscovery"
import { DashboardPage } from "@/pages/DashboardPage"
import { DeploymentRouts } from "@/pages/DeploymentsPage"
import { DiscoveryPage } from "@/pages/DiscoveryPage"
import { GovernancePage } from "@/pages/GovernancePage"
import { IntegrationsPage } from "@/pages/IntegrationsPage"
import {
  PolicyExceptionLayout,
  PolicyExceptionLinkRedirect,
  PolicyExceptionPage
} from "@/pages/PolicyExceptionPage"
import { ReportsRoutes } from "@/pages/ReportsPage"
import { ScmCallbackPage, ScmSignInStartPage } from "@/pages/ScmSignInPage"
import { SettingsRouter } from "@/pages/SettingsPage"
import { isKodemSsoPopup } from "@/pages/SignInPage/applications/authenticate"
import { AcsAuth } from "@/pages/SignInPage/components/organisms/AcsAuth"
import { ApiDocsAuth } from "@/pages/SignInPage/components/organisms/ApiDocsAuth"
import { CliAuth } from "@/pages/SignInPage/components/organisms/CliAuth"
import { ForgotPasswordForm } from "@/pages/SignInPage/components/organisms/ForgotPasswordForm"
import { McpAuth } from "@/pages/SignInPage/components/organisms/McpAuth"
import { OrganizationSlugForm } from "@/pages/SignInPage/components/organisms/OrganizationSlugForm"
import { ResetPasswordForm } from "@/pages/SignInPage/components/organisms/ResetPasswordForm"
import { SignInForm } from "@/pages/SignInPage/components/organisms/SignInForm"
import { WelcomeCard } from "@/pages/SignInPage/components/organisms/WelcomeCard"
import { SignInPageTemplate } from "@/pages/SignInPage/components/templates/SignInPageTemplate"
import { SsoPopupTemplate } from "@/pages/SignInPage/components/templates/SsoPopupTemplate"
import { TenantSignupPage } from "@/pages/TenantSignupPage/TenantSignupPage"
import { TriagePage } from "@/pages/TriagePage"
import { LayoutTemplate } from "@/templates/LayoutTemplate"
import { MinimalLayoutTemplate } from "@/templates/MinimalLayoutTemplate"
import { isSelfHosted } from "./helpers/self-hosted.helper"

const App = () => {
  const { user } = useUser()
  const [searchParams] = useSearchParams()
  const { pathname } = useLocation()
  const redirectToUrl = searchParams.get(REDIRECT_URL_PARAM_AUTH) || "/"

  useInitApp()
  useServicesWithUser()
  useLoggerOnNavigation()
  useCheckApplicationUpdates()

  const authorizedUser = isAuthorizedUser(user)

  const {
    userNeedVerifyPhoneNumberMultiFactor,
    userNeedVerificationCodeMultiFactor
  } = user
  const userNeedUseMultiFactor =
    userNeedVerifyPhoneNumberMultiFactor || userNeedVerificationCodeMultiFactor
  const showApplication = authorizedUser && !userNeedUseMultiFactor

  const {
    INTEGRATIONS,
    DISCOVERY,
    TRIAGE,
    DEPLOYMENTS,
    POLICY_EXCEPTION,
    SCM_SIGN_IN
  } = NAMES_ROUTES
  const selfHosted = isSelfHosted()

  // A Kodem SSO popup only hands its sign-in back, even when the windows already share a session:
  // the app never starts in it
  if (isKodemSsoPopup() && matchPath("/:slug/acs", pathname))
    return (
      <Routes>
        <Route element={<SsoPopupTemplate />}>
          <Route path=":slug/acs" element={<AcsAuth />} />
        </Route>
      </Routes>
    )

  if (
    showApplication &&
    ((!selfHosted && redirectToUrl === EXTERNAL_ROUTES.API_DOCS) ||
      pathname === EXTERNAL_ROUTES.CLI_AUTH ||
      pathname === EXTERNAL_ROUTES.MCP_AUTH)
  )
    return (
      <Routes>
        <Route element={<SignInPageTemplate />}>
          <Route path={EXTERNAL_ROUTES.CLI_AUTH} element={<CliAuth />} />
          <Route path={EXTERNAL_ROUTES.MCP_AUTH} element={<McpAuth />} />
          <Route path="*" element={<ApiDocsAuth />} />
        </Route>
      </Routes>
    )

  if (!showApplication)
    return (
      <Routes>
        <Route element={<SignInPageTemplate />}>
          <Route
            path=":slug/forgot-password"
            element={<ForgotPasswordForm />}
          />
          <Route path=":slug/signin" element={<SignInForm user={user} />} />
          <Route path=":slug/invite" element={<WelcomeCard />} />
          <Route path=":slug/reset-password" element={<ResetPasswordForm />} />
          <Route path=":slug/acs" element={<AcsAuth />} />
          {!selfHosted && (
            <>
              <Route path="/signup" element={<TenantSignupPage />} />
              <Route
                path={`/${INTEGRATIONS.ROOT}/${INTEGRATIONS.CALLBACK}`}
                element={<IntegrationsPage authorizedUser={authorizedUser} />}
              />
            </>
          )}
          <Route path="/*" element={<OrganizationSlugForm />} />
        </Route>
        <Route path="/reports-generate" element={<Reports />} />
        <Route
          path={`:slug/${POLICY_EXCEPTION.ROOT}/:scanId`}
          element={<PolicyExceptionLinkRedirect authorizedUser={false} />}
        />
        {/* Developers without a Kodem account, signed in with their SCM */}
        <Route element={<MinimalLayoutTemplate />}>
          <Route
            path={`${SCM_SIGN_IN.ROOT}/${SCM_SIGN_IN.START}`}
            element={<ScmSignInStartPage />}
          />
          <Route
            path={`${SCM_SIGN_IN.ROOT}/${SCM_SIGN_IN.CALLBACK}`}
            element={<ScmCallbackPage />}
          />
          <Route
            path={`${POLICY_EXCEPTION.ROOT}/:scanId`}
            element={<PolicyExceptionPage mode="scm" />}
          />
        </Route>
      </Routes>
    )

  return (
    <TicketingProvider>
      <KaiProvider>
        <Routes>
          <Route
            path={`:slug/${POLICY_EXCEPTION.ROOT}/:scanId`}
            element={<PolicyExceptionLinkRedirect authorizedUser />}
          />
          {/* A Kodem session on this host must not swallow another region's SCM handoff */}
          <Route element={<MinimalLayoutTemplate />}>
            <Route
              path={`${SCM_SIGN_IN.ROOT}/${SCM_SIGN_IN.CALLBACK}`}
              element={<ScmCallbackPage />}
            />
          </Route>
          {/* Opened from the PR report link only; no menu item */}
          <Route element={<PolicyExceptionLayout />}>
            <Route
              path={`${POLICY_EXCEPTION.ROOT}/:scanId`}
              element={
                <Suspense fallback={<Loader />}>
                  <PolicyExceptionPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<LayoutTemplate />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path={`${DISCOVERY.ROOT}/*`} element={<DiscoveryPage />} />
            <Route path={`${TRIAGE.ROOT}/*`} element={<TriagePage />} />
            <Route path="governance/*" element={<GovernancePage />} />
            <Route
              path={`${DEPLOYMENTS.ROOT}/*`}
              element={<DeploymentRouts />}
            />
            <Route
              path={`${INTEGRATIONS.ROOT}/*`}
              element={<IntegrationsPage authorizedUser={authorizedUser} />}
            />
            <Route path="settings/*" element={<SettingsRouter />} />
            <Route path={`/ai/:tab`} element={<AiDiscovery />} />
            {!selfHosted && (
              <Route path="reports/*" element={<ReportsRoutes />} />
            )}
            <Route
              path="/*"
              element={<Navigate to={redirectToUrl} replace />}
            />
          </Route>
        </Routes>
        <SplashScreen />
      </KaiProvider>
    </TicketingProvider>
  )
}

export default App
